import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Controlled search input value synced (debounced) to the `?search=` URL param.
 * Returns [inputValue, setInputValue, appliedSearch], where appliedSearch is
 * the value currently in the URL.
 */
const useDebouncedUrlSearch = (delay, paramName = 'search') => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get(paramName) || '';
  const [value, setValue] = useState(urlSearch);
  const lastPushed = useRef(urlSearch);

  // URL changed from outside (tab switch, back/forward navigation)
  useEffect(() => {
    if (urlSearch !== lastPushed.current) {
      lastPushed.current = urlSearch;
      setValue(urlSearch);
    }
  }, [urlSearch]);

  useEffect(() => {
    if (value === lastPushed.current) return undefined;

    const timer = setTimeout(() => {
      lastPushed.current = value;
      const nextParams = new URLSearchParams(window.location.search);
      if (value) {
        nextParams.set(paramName, value);
      } else {
        nextParams.delete(paramName);
      }
      setSearchParams(nextParams, { replace: true });
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return [value, setValue, urlSearch];
};

export default useDebouncedUrlSearch;
